import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { BiEditAlt } from "react-icons/bi";
import CreateLocations from "./addLocations";
import UploadLocationsCsvFile from "./uploadLocationsCsvFile";
import LocationsCsvFormat from "./locationsCsvFormat";
import { Pagination } from "react-bootstrap";
import "./hm-theme.css";

function GeoLocations({ selectedOrgcode }) {
  const [geoLocations, setGeoLocations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locationId, setLocationId] = useState(null);
  const [loader, setLoader] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [company, setCompany] = useState(null);

  const token = JSON.parse(localStorage.getItem("data"))?.token;

  const getData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const companyRes = await axios.get(`/api/companydetails/companycode=${selectedOrgcode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompany(companyRes.data);
      const response = await axios.get(`/api/geolocation/getAllLocations/companyId=${companyRes.data?.id}`, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      setGeoLocations(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const navigate = useNavigate();
  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    getData(newPage);
  };

  const handleCreateLocation = () => {
    setLocationId(null);
    setIsModalOpen(true);
  };

  const handleEditLocation = (id) => {
    setLocationId(id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    getData();
  };

  useEffect(() => {
    getData(currentPage);
  }, []);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    if (currentPage >= totalPages - 3) startPage = Math.max(0, totalPages - maxPageNumbersToShow);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  const [isCsvUplaodModalOpen, setIsCsvUplaodModalOpen] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const handleShowImageModal = () => setShowImageModal(true);
  const handleCloseImageModal = () => setShowImageModal(false);

  const uploadCsv = () => {
    setIsCsvUplaodModalOpen(true);
  };

  const closeUploadModal = () => {
    setIsCsvUplaodModalOpen(false);
    getData();
  };

  return (
    <div>
      {loader && <div className="loading"></div>}

      <div className="hm-card">
        {/* Header */}
        <div className="hm-card-header">
          <h3 className="hm-card-title">
            <i className="material-icons">location_on</i>
            Locations
          </h3>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-primary rounded-pill px-3" onClick={handleShowImageModal}>
              View Format
            </button>
            <button className="btn btn-outline-secondary rounded-pill px-3" onClick={uploadCsv}>
              Upload CSV
            </button>
            <button className="btn-hm-primary" onClick={handleCreateLocation}>
              <i className="material-icons" style={{ fontSize: "1rem" }}>add</i>
              Add Location
            </button>
          </div>
        </div>

        {/* Card Body & Table */}
        <div className="hm-card-body p-0">
          <div style={{ overflowX: "auto" }}>
            <table className="hm-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th style={{ width: "120px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {geoLocations.length === 0 ? (
                  <tr>
                    <td colSpan="2">
                      <div className="hm-empty-state">
                        <i className="material-icons">location_off</i>
                        <p>{loader ? "Loading locations..." : "No Data Available"}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  geoLocations.map((loc) => (
                    <tr key={loc.id}>
                      <td className="fw-semibold">{loc.location}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm rounded-circle p-1 d-inline-flex align-items-center justify-content-center"
                          style={{ width: "32px", height: "32px" }}
                          title="Edit Location"
                          onClick={() => handleEditLocation(loc.id)}
                        >
                          <BiEditAlt size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center py-3">
              <Pagination className="hm-pagination">
                <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                {renderPageNumbers()}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      {isCsvUplaodModalOpen && (
        <UploadLocationsCsvFile isOpen={isCsvUplaodModalOpen} onRequestClose={closeUploadModal} companycode={selectedOrgcode} companyId={company?.id} />
      )}

      {showImageModal && (
        <LocationsCsvFormat isOpen={showImageModal} onRequestClose={handleCloseImageModal} />
      )}

      {isModalOpen && (
        <CreateLocations
          isOpen={isModalOpen}
          onRequestClose={handleCloseModal}
          locationId={locationId}
          companycode={selectedOrgcode}
          companyId={company?.id}
        />
      )}
    </div>
  );
}

export default GeoLocations;

